import { useRef, useState } from 'react';
import { readApiError, roverNotificationsApi } from '@/api/Rover/notifications/roverNotifications';
import type {
  MessageOption,
  OptionsFormat,
  SelectionMode,
  StatusNotificationConfig,
} from '@/api/Rover/notifications/roverNotifications.types';
import { Button, ConfirmDialog, cx, Notice, Segmented, Switch } from '../ui/primitives';
import { MergeFieldBar } from './MergeFieldBar';

interface Props {
  formId: string;
  config: StatusNotificationConfig;
  options: MessageOption[];
  onOptionsChange: (next: MessageOption[]) => void;
  onSettingsChange: (patch: { optionsSelectionMode?: SelectionMode; optionsFormat?: OptionsFormat }) => Promise<void>;
  canEdit: boolean;
  /** When local, option edits stay in React state until the parent saves the status notification. */
  optionsPersistence?: 'api' | 'local';
}

type Draft = { id: string | 'new'; label: string; messageText: string };

export function MessageOptionsManager({
  formId,
  config,
  options,
  onOptionsChange,
  onSettingsChange,
  canEdit,
  optionsPersistence = 'api',
}: Props) {
  const { status } = config;
  const [draft, setDraft] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<MessageOption | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);

  const sorted = [...options].sort((a, b) => a.sortOrder - b.sortOrder);
  const activeCount = sorted.filter((o) => o.isActive).length;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      const { message, fields } = readApiError(e);
      setError(message);
      if (fields) setErrors(fields);
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    run(async () => {
      if (!draft) return;
      if (!draft.label.trim()) {
        setErrors({ label: 'Label is required' });
        return;
      }
      setErrors({});
      if (optionsPersistence === 'local') {
        if (draft.id === 'new') {
          const created: MessageOption = {
            id: `local_${Date.now()}`,
            label: draft.label.trim(),
            messageText: draft.messageText,
            isActive: true,
            sortOrder: options.length,
          };
          onOptionsChange([...options, created]);
        } else {
          onOptionsChange(
            options.map((o) => (o.id === draft.id ? { ...o, label: draft.label.trim(), messageText: draft.messageText } : o))
          );
        }
      } else if (draft.id === 'new') {
        const created = await roverNotificationsApi.createOption(formId, status, {
          label: draft.label.trim(),
          messageText: draft.messageText,
        });
        onOptionsChange([...options, created]);
      } else {
        const updated = await roverNotificationsApi.updateOption(formId, status, draft.id, {
          label: draft.label.trim(),
          messageText: draft.messageText,
        });
        onOptionsChange(options.map((o) => (o.id === updated.id ? updated : o)));
      }
      setDraft(null);
    });

  const toggleActive = (o: MessageOption) =>
    run(async () => {
      if (optionsPersistence === 'local') {
        onOptionsChange(options.map((x) => (x.id === o.id ? { ...x, isActive: !x.isActive } : x)));
        return;
      }
      const updated = await roverNotificationsApi.updateOption(formId, status, o.id, { isActive: !o.isActive });
      onOptionsChange(options.map((x) => (x.id === updated.id ? updated : x)));
    });

  const move = (index: number, dir: -1 | 1) =>
    run(async () => {
      const ids = sorted.map((o) => o.id);
      const target = index + dir;
      if (target < 0 || target >= ids.length) return;
      [ids[index], ids[target]] = [ids[target], ids[index]];
      const reordered = sorted.map((o) => ({ ...o, sortOrder: ids.indexOf(o.id) }));
      onOptionsChange(reordered);
      if (optionsPersistence !== 'local') {
        onOptionsChange(await roverNotificationsApi.reorderOptions(formId, status, ids));
      }
    });

  const remove = (o: MessageOption) =>
    run(async () => {
      if (optionsPersistence !== 'local') {
        await roverNotificationsApi.deleteOption(formId, status, o.id);
      }
      onOptionsChange(options.filter((x) => x.id !== o.id));
      setConfirmDelete(null);
    });

  const insertToken = (token: string) => {
    if (!draft) return;
    const el = textRef.current;
    const start = el?.selectionStart ?? draft.messageText.length;
    const end = el?.selectionEnd ?? start;
    const next = draft.messageText.slice(0, start) + token + draft.messageText.slice(end);
    setDraft({ ...draft, messageText: next });
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const renderForm = () =>
    draft && (
      <div className='flex flex-col gap-4 border-b border-gray-200 bg-emerald-50/60 px-4 py-5 sm:pl-14'>
        <span className='text-sm font-semibold text-emerald-900'>{draft.id === 'new' ? 'New option' : 'Edit option'}</span>
        <label className='flex flex-col gap-1.5 text-sm font-semibold text-gray-700'>
          Label (staff see this and it's used in SMS)
          <input
            type='text'
            value={draft.label}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            placeholder='e.g. Income exceeds program limit'
            aria-invalid={!!errors.label}
            className='h-11 rounded-md border border-gray-300 bg-white px-3 text-[15px] font-normal text-gray-900'
          />
          {errors.label && <span className='text-sm font-normal text-red-700'>{errors.label}</span>}
        </label>
        <label className='flex flex-col gap-1.5 text-sm font-semibold text-gray-700'>
          Message text (goes into the email and on-screen message)
          <textarea
            ref={textRef}
            rows={3}
            value={draft.messageText}
            onChange={(e) => setDraft({ ...draft, messageText: e.target.value })}
            placeholder='Write the sentence the applicant will read.'
            className='rounded-md border border-gray-300 bg-white p-3 text-[15px] font-normal leading-relaxed text-gray-900'
          />
        </label>
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <MergeFieldBar onInsert={insertToken} exclude={['selected_options']} compact />
          <div className='flex gap-3'>
            <Button onClick={() => (setDraft(null), setErrors({}))}>Cancel</Button>
            <Button variant='primary' onClick={save} disabled={busy}>
              Save option
            </Button>
          </div>
        </div>
      </div>
    );

  return (
    <div className='flex flex-col gap-5'>
      <div className='flex flex-col divide-y divide-gray-200 rounded-lg border border-gray-200'>
        <div className='flex flex-wrap items-center justify-between gap-3 px-4 py-3'>
          <span className='text-sm font-semibold text-gray-700'>Staff can select</span>
          <Segmented<SelectionMode>
            label='Staff can select'
            value={config.optionsSelectionMode}
            onChange={(v) => canEdit && void onSettingsChange({ optionsSelectionMode: v })}
            options={[
              { value: 'single', label: 'One option' },
              { value: 'multiple', label: 'Multiple options' },
            ]}
          />
        </div>
        <div className='flex flex-wrap items-center justify-between gap-3 px-4 py-3'>
          <span className='text-sm font-semibold text-gray-700'>Selection required</span>
          <div className='flex items-center gap-3'>
            <span className='text-sm leading-snug text-gray-600'>Always on. Staff must pick before the status changes.</span>
            <Switch checked disabled onChange={() => undefined} label='Selection required (always on)' />
          </div>
        </div>
        <div className='flex flex-wrap items-center justify-between gap-3 px-4 py-3'>
          <span className='text-sm font-semibold text-gray-700'>How options appear in email and on-screen</span>
          <Segmented<OptionsFormat>
            label='Options format'
            value={config.optionsFormat}
            onChange={(v) => canEdit && void onSettingsChange({ optionsFormat: v })}
            options={[
              { value: 'bullets', label: 'Bulleted list' },
              { value: 'paragraph', label: 'Paragraph' },
            ]}
          />
        </div>
      </div>

      <div className='flex items-end justify-between gap-4'>
        <div className='flex flex-col gap-1'>
          <h3 className='text-base font-semibold text-gray-900'>
            Options <span className='font-normal text-gray-600'>({activeCount} active)</span>
          </h3>
          <p className='text-sm text-gray-600'>
            The label is what staff see in the modal and what goes in SMS. The message text goes into the email and on-screen message.
          </p>
        </div>
        {canEdit && (
          <Button variant='outline' onClick={() => setDraft({ id: 'new', label: '', messageText: '' })} disabled={!!draft}>
            + Add option
          </Button>
        )}
      </div>

      {error && <Notice tone='danger'>{error}</Notice>}

      <div className='overflow-hidden rounded-lg border border-gray-200'>
        {draft?.id === 'new' && renderForm()}
        {sorted.length === 0 && !draft && (
          <p className='px-4 py-8 text-center text-sm text-gray-600'>
            No options yet. Without options, this status sends its custom or default message right away.
          </p>
        )}
        <ul>
          {sorted.map((o, i) =>
            draft?.id === o.id ? (
              <li key={o.id}>{renderForm()}</li>
            ) : (
              <li
                key={o.id}
                className={cx('flex items-center gap-3 border-b border-gray-200 px-4 py-3 last:border-b-0', !o.isActive && 'bg-gray-50')}
              >
                {canEdit && (
                  <div className='flex flex-col'>
                    <button
                      type='button'
                      aria-label={`Move ${o.label} up`}
                      disabled={i === 0 || busy}
                      onClick={() => move(i, -1)}
                      className='flex h-6 w-8 items-center justify-center rounded text-gray-600 hover:bg-gray-100 disabled:opacity-30'
                    >
                      ▲
                    </button>
                    <button
                      type='button'
                      aria-label={`Move ${o.label} down`}
                      disabled={i === sorted.length - 1 || busy}
                      onClick={() => move(i, 1)}
                      className='flex h-6 w-8 items-center justify-center rounded text-gray-600 hover:bg-gray-100 disabled:opacity-30'
                    >
                      ▼
                    </button>
                  </div>
                )}
                <span className='w-6 text-sm text-gray-600'>{i + 1}</span>
                <div className={cx('flex min-w-0 flex-1 flex-col gap-0.5', !o.isActive && 'opacity-70')}>
                  <span className='text-[15px] font-semibold text-gray-900'>{o.label}</span>
                  <span className='text-sm leading-snug text-gray-600'>{o.messageText}</span>
                </div>
                <Switch checked={o.isActive} disabled={!canEdit || busy} onChange={() => toggleActive(o)} label={`${o.label} active`} />
                {canEdit && (
                  <div className='flex gap-1'>
                    <Button
                      variant='ghost'
                      className='!px-3'
                      aria-label={`Edit ${o.label}`}
                      disabled={!!draft}
                      onClick={() => setDraft({ id: o.id, label: o.label, messageText: o.messageText })}
                    >
                      Edit
                    </Button>
                    <Button
                      variant='ghost'
                      className='!px-3 text-red-700'
                      aria-label={`Delete ${o.label}`}
                      onClick={() => setConfirmDelete(o)}
                    >
                      Delete
                    </Button>
                  </div>
                )}
              </li>
            )
          )}
        </ul>
      </div>
      <p className='text-sm text-gray-600'>Inactive options stay saved but don't appear in the status-change modal.</p>

      <ConfirmDialog
        open={!!confirmDelete}
        title='Delete option?'
        message={
          <>
            <strong>{confirmDelete?.label}</strong> will be removed from this status. Past notifications that used it are not affected.
          </>
        }
        confirmLabel='Delete option'
        tone='danger'
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && void remove(confirmDelete)}
      />
    </div>
  );
}
